import { Box, Theme, ThemeProvider, Typography } from "@mui/material";
import groundcover from "@groundcover/browser";
import { Suspense, useEffect, useRef, useState } from "react";
import { hotjar } from "react-hotjar";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { Config } from "stere-insurance-sdk/dist/types";

import { fetchFactoryFunctionsAPI } from "api/siaApplication";

import GlobalLoader from "components/loader/GlobalLoader";
import CircularProgressLoader from "components/loader/Loader";
import {
  CORX,
  ENVIRONMENT,
  IS_HOSTED_INSURANCE,
  PORTAL_NAME,
} from "config";
import {
  fetchAvailableProducts,
  fetchClientFeatures,
  saveClientSentInfo,
  saveNoQuoteText,
  setApplicationConfig,
  setClientUrl,
  setExternalAuthConfig,
  setProductConfig,
  setQuoteConfig,
  setSectionHeaderInfoConfig,
  setShowAutoRenewalSwitch,
  setShowPaymentSummaryPage,
  setSimulateErrorConfig,
  setSupportEmail,
  toggleAutoRenewalConsent,
} from "store/features/clientSlice";
import { setLoaderText, updateInitialLoader } from "store/features/loaderSlice";
import { setCurentStep } from "store/features/stepperSlice";
import { setCustomStyles } from "store/features/stylesSlice";
import { setScriptRef } from "store/features/surveyJsSlice";
import { customStylesSelector } from "store/selectors/selectors";
import { RootState } from "store/store";
import { baseMuiTheme, getTheme, setMuiTheme } from "theme";
import { useT } from "translation";

import { sendMessageToClient } from "utils/utils";

import "./App.css";

import AppRoutes from "AppRoutes";
import ErrorComponent from "components/common/ErrorComponent/ErrorComponent";
import flagsmith from "flagsmith";
import { FlagsmithProvider } from "flagsmith/react";
import { portalConfigSelector } from "portalConfig/selectors";
import useSetupPortalConfig from "portalConfig/useSetupPortalConfig";
import { setIsFlagSmithLoading } from "store/features/flagSmithSlice";
import useTokenListener from "hooks/useTokenListener";
import useCookieListener from "hooks/useCookieListener";

const HOTJAR_VERSION = 6;

function App() {
  const { t } = useT();
  const dispatch = useDispatch();
  useTokenListener(useCookieListener());
  const [theme, setTheme] = useState<Theme>(baseMuiTheme);
  const customStyles = useSelector(customStylesSelector);

  const selectedAccount = useSelector(
    (state: RootState) => state.auth.accounts.selectedAccount
  );
  useSetupPortalConfig({
    clientName: PORTAL_NAME,
  });
  const {
    data: config,
    loading,
    error: configError,
  } = useSelector(portalConfigSelector);

  const { clientAPIKey, productSelection, externalAuthConfig } = useSelector(
    (state: RootState) => {
      return state.client;
    }
  );

  // Pre-fetching all factory functions and adding them as a script for the Application step.
  const script = useRef(document.createElement("script"));
  const registerFactoryFunctions = async () => {
    try {
      const factoryFunctions = await fetchFactoryFunctionsAPI(externalAuthConfig ? null : clientAPIKey);
      script.current.text = factoryFunctions?.data;
      document.head.appendChild(script.current);
      localStorage.setItem("scriptLoaded", "true");
      localStorage.removeItem("scriptCallMade");
      dispatch(setScriptRef(script));
    } catch (error) {
      console.error(error);
    }
  };

  // Setting css root values for the SurveyJS component/form
  const changeCSSRootValues = () => {
    const root = document.documentElement;
    root.style.setProperty("--default-background", theme.palette.primary.main);
    root.style.setProperty("--font-family", theme.typography.fontFamily);
    root.style.setProperty("--input-label-color", customStyles?.input?.color);
    root.style.setProperty(
      "--input-box-bg-color",
      customStyles?.input?.backgroundColor
    );
    root.style.setProperty("--input-text-color", customStyles?.input?.color);
    root.style.setProperty(
      "--input-focus-border-color",
      customStyles?.input?.active?.borderColor
    );
    root.style.setProperty(
      "--input-border-color",
      customStyles?.input?.borderColor || "transparent"
    );
    root.style.setProperty(
      "--input-checkbox-border-color",
      customStyles?.input?.borderColor ||
      customStyles?.input?.hover?.borderColor ||
      customStyles?.input?.active?.borderColor ||
      "transparent"
    );
    root.style.setProperty(
      "--input-hover-border-color",
      customStyles?.input?.hover?.borderColor
    );
    root.style.setProperty(
      "--animation-color",
      customStyles.muiTheme.palette.primary.main
    );
  };

  // Method that receives client data/styles and applies it to the app
  const setClientReceivedStyles = (event: { origin: string; data: any }) => {
    const data = event.data as Config;
    if (data.auth) {
      dispatch(saveClientSentInfo(data.auth));

      if (data.hotjarConfig) {
        hotjar.initialize({
          id: Number(data.hotjarConfig?.hotjarID),
          sv: HOTJAR_VERSION,
        });
      }

      groundcover.init({
        apiKey: process.env.REACT_APP_GROUNDCOVER_API_KEY,
        cluster: process.env.REACT_APP_GROUNDCOVER_CLUSTER,
        environment: process.env.REACT_APP_ENV,
        dsn: process.env.REACT_APP_GROUNDCOVER_DSN,
        appId: process.env.REACT_APP_GROUNDCOVER_APP_ID,
      });

      if (data.clientUrl) {
        dispatch(setClientUrl(data.clientUrl));
      }

      if (data.data) {
        localStorage.setItem("clientData", JSON.stringify(data.data));
      }

      if (data.sectionHeaderInfo) {
        dispatch(setSectionHeaderInfoConfig(data.sectionHeaderInfo));
      }

      if (data.showPaymentSummaryPage === false) {
        dispatch(setShowPaymentSummaryPage(false));
      }

      if (data.showAutoRenewalSwitch === true) {
        dispatch(setShowAutoRenewalSwitch(true));
        dispatch(toggleAutoRenewalConsent(true));
      }

      if (data.simulateError) {
        dispatch(setSimulateErrorConfig(data.simulateError));
      }

      if (data.applicationConfig) {
        dispatch(setApplicationConfig(data.applicationConfig));
      }

      if (data.productConfig) {
        dispatch(setProductConfig(data.productConfig));
      }

      if (data.quoteConfig) {
        dispatch(setQuoteConfig(data.quoteConfig));
      }

      if (data.supportEmail) {
        dispatch(setSupportEmail(data.supportEmail));
      }
    }

    if (data.externalAuthConfig) {
      dispatch(setExternalAuthConfig(data.externalAuthConfig));
    }

    if (data.noQuoteText) {
      dispatch(saveNoQuoteText(data.noQuoteText));
    }

    if (data.styles) {
      setMuiTheme(data.styles);
      const { appTheme, ...finalTheme } = getTheme();
      dispatch(setCustomStyles(finalTheme));
      setTheme(appTheme);
      // to hide globalLoader screen for iframe app
      setTimeout(() => {
        dispatch(updateInitialLoader(false));
        sendMessageToClient(
          { type: "SHOW_IFRAME", details: {} },
          data.clientUrl
        );
      }, 2000);
    }
  };

  useEffect(() => {
    localStorage.removeItem("scriptCallMade");
    localStorage.removeItem("scriptLoaded");
    localStorage.removeItem("clientData");
    localStorage.removeItem("prefillData");
    sessionStorage.removeItem("template_slug");
    sessionStorage.removeItem("policyId");

    const receiveMessage = setClientReceivedStyles;
    window.addEventListener("message", receiveMessage);

    return () => {
      window.removeEventListener("message", receiveMessage);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    changeCSSRootValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const isAccountAvailable = Boolean(selectedAccount);
  // Useeffect that fetches all available products for a client as soon as the clientAPIKey has been received
  useEffect(() => {
    if (
      ((IS_HOSTED_INSURANCE && externalAuthConfig?.partner !== 'fairfax') || isAccountAvailable) &&
      clientAPIKey &&
      !window?.location?.href?.includes("/application/payment?")
    ) {
      dispatch(setLoaderText(t("FETCHING_PRODUCTS")));
      if (PORTAL_NAME !== CORX) {
        dispatch(fetchAvailableProducts());
      }
      if (IS_HOSTED_INSURANCE) {
        dispatch(fetchClientFeatures());
      }

      // Attaching factory functions script
      localStorage.setItem("scriptCallMade", "true");
      registerFactoryFunctions();
    }
    // to hide globalLoader screen for portal app
    setTimeout(() => {
      if (!clientAPIKey) {
        dispatch(updateInitialLoader(false));
      }
    }, 2000);
    // redirect to applications section if client has sent products
    if (clientAPIKey && productSelection?.length) {
      dispatch(setCurentStep(1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientAPIKey, externalAuthConfig, isAccountAvailable]);

  useEffect(() => {
    if (config?.styles && !IS_HOSTED_INSURANCE) {
      setMuiTheme(config.styles);
      const { appTheme, ...finalTheme } = getTheme();
      dispatch(setCustomStyles(finalTheme));
      setTheme(appTheme);
    }
  }, [config?.styles, dispatch]);
  useEffect(() => {
    const initializeFlagsmith = async () => {
      try {
        await flagsmith.init({
          environmentID: process.env.REACT_APP_FLAGSMITH_ENV_ID,
        });
        if (!IS_HOSTED_INSURANCE) {
          const traits = { portal_name: PORTAL_NAME };
          flagsmith.identify(PORTAL_NAME, traits);
        }
      } catch (error) {
        console.error("Error initializing Flagsmith:", error);
      } finally {
        dispatch(setIsFlagSmithLoading(false));
      }
    };

    dispatch(setIsFlagSmithLoading(true));
    initializeFlagsmith();
  }, [dispatch]);

  const isConfigLoading = loading && !IS_HOSTED_INSURANCE;
  const isConfigError =
    !isConfigLoading && Boolean(configError) && !IS_HOSTED_INSURANCE;

  if (isConfigError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <ErrorComponent />
        {ENVIRONMENT !== 'production' && (
          <Box>
            <Typography>
              {configError?.message}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Suspense fallback={<CircularProgressLoader />}>
      <ThemeProvider theme={theme}>
        {!window?.location?.href?.includes("/application/payment?") && (
          <GlobalLoader />
        )}

        <FlagsmithProvider flagsmith={flagsmith}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </FlagsmithProvider>
      </ThemeProvider>
    </Suspense>
  );
}

export default App;
